// ═══════════════════════════════════════════════════════════════════════════════
// PWA inner scroll — keep focused inputs visible above the software keyboard
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, type RefObject } from 'react';
import {
  scheduleFormFieldFocusScroll,
  scrollFormFieldIntoView,
  shouldUsePwaFocusScrollForField,
} from '../lib/pwaInputFocusScroll';
import { isVirtualKeyboardOpen } from '../lib/pwaShellHeight';

export const usePwaInputFocusScroll = (
  enabled: boolean,
  scrollContainerRef: RefObject<HTMLDivElement | null>,
): void => {
  const scrollFieldIntoView = useCallback(
    (target: HTMLElement): void => {
      const scrollRoot = scrollContainerRef.current;
      if (!scrollRoot?.contains(target)) {
        return;
      }
      scrollFormFieldIntoView(scrollRoot, target);
    },
    [scrollContainerRef],
  );

  const scrollActiveField = useCallback((): void => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !shouldUsePwaFocusScrollForField(active)) {
      return;
    }
    scrollFieldIntoView(active);
  }, [scrollFieldIntoView]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleFocusIn = (event: FocusEvent): void => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !shouldUsePwaFocusScrollForField(target)) {
        return;
      }

      const scrollRoot = scrollContainerRef.current;
      if (!scrollRoot?.contains(target)) {
        return;
      }

      scheduleFormFieldFocusScroll(scrollRoot, target);
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [enabled, scrollContainerRef]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleViewportChange = (): void => {
      if (!isVirtualKeyboardOpen()) {
        return;
      }
      const active = document.activeElement;
      if (active instanceof HTMLTextAreaElement) {
        return;
      }
      requestAnimationFrame(() => {
        scrollActiveField();
        window.setTimeout(scrollActiveField, 50);
      });
    };

    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', handleViewportChange);
    return () => {
      visualViewport?.removeEventListener('resize', handleViewportChange);
    };
  }, [enabled, scrollActiveField]);
};
